export const MAX_PRINT_POINTS = 5;
export const DEFAULT_PRINT_POINT_ID = 'caixa';

const cleanText = (value) => String(value || '').trim();

const normalizePoint = (point, index) => {
  const printerId = cleanText(point?.printerId || point?.printerName);
  const isDefault = point?.id === DEFAULT_PRINT_POINT_ID || point?.isDefault === true || (index === 0 && point?.name === 'Caixa');

  return {
    id: isDefault ? DEFAULT_PRINT_POINT_ID : cleanText(point?.id) || `ponto-${index + 1}`,
    name: isDefault ? 'Caixa' : cleanText(point?.name),
    printerId,
    enabled: point?.enabled !== false,
    isDefault,
  };
};

// Keeps legacy browser settings working by turning their single printer into the
// immutable default point, "Caixa". The legacy printerId is deliberately kept
// at the settings root by callers for older versions of the application.
export const normalizePrintPoints = (settings = {}) => {
  const legacyPrinterId = cleanText(settings.printerId || settings.printerName);
  const rawPoints = Array.isArray(settings.printPoints) ? settings.printPoints : [];
  const normalized = rawPoints
    .map(normalizePoint)
    .filter((point) => point.name || point.isDefault);

  const suppliedDefault = normalized.find((point) => point.isDefault || point.id === DEFAULT_PRINT_POINT_ID);
  const otherPoints = normalized.filter((point) => point !== suppliedDefault && point.id !== DEFAULT_PRINT_POINT_ID);
  const caixa = {
    id: DEFAULT_PRINT_POINT_ID,
    name: 'Caixa',
    printerId: cleanText(suppliedDefault?.printerId || legacyPrinterId),
    // Caixa is the compatibility destination and must remain available.
    enabled: true,
    isDefault: true,
  };

  return [caixa, ...otherPoints]
    .filter((point) => point.id === DEFAULT_PRINT_POINT_ID || point.name)
    .filter((point, index, list) => list.findIndex((candidate) => candidate.id === point.id) === index)
    .filter((point, index, list) => point.isDefault || list.findIndex((candidate) => candidate.name.toLocaleLowerCase('pt-BR') === point.name.toLocaleLowerCase('pt-BR')) === index)
    .slice(0, MAX_PRINT_POINTS);
};

export const getActivePrintPoints = (settings = {}) => normalizePrintPoints(settings)
  .filter((point) => point.enabled && cleanText(point.printerId));

export const createPrintPointId = () => `ponto-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const toPrintPointRegistryPayload = (settings = {}) => normalizePrintPoints(settings).map((point) => ({
  id: point.id,
  name: point.name,
  enabled: point.enabled !== false,
}));

// The backend owns the registry used by tables, while printer credentials and
// layout remain browser-local. Merge by stable point id so a server refresh
// never leaks or overwrites printerId/CIBD preferences.
export const mergeServerPrintPoints = (settings = {}, serverPoints = []) => {
  const localPoints = normalizePrintPoints(settings);
  const localById = new Map(localPoints.map((point) => [point.id, point]));
  const registryPoints = Array.isArray(serverPoints) ? serverPoints : [];
  const serverSettings = {
    printPoints: registryPoints.map((point) => ({
      id: cleanText(point?.id),
      name: cleanText(point?.name),
      enabled: point?.enabled !== false,
      isDefault: point?.isDefault === true || cleanText(point?.id) === DEFAULT_PRINT_POINT_ID,
    })),
  };

  const mergedPoints = normalizePrintPoints(serverSettings).map((point) => ({
    ...point,
    printerId: cleanText(localById.get(point.id)?.printerId),
  }));

  return {
    ...settings,
    printPoints: mergedPoints,
    printerId: mergedPoints.find((point) => point.id === DEFAULT_PRINT_POINT_ID)?.printerId
      || cleanText(settings.printerId || settings.printerName),
  };
};

export const getTablePrintPointIds = (order = {}) => {
  const directIds = order?.tablePrintPointIds;
  const metadataIds = order?.additionalInfo?.printing?.printPointIds;
  const snapshot = Array.isArray(directIds) ? directIds : Array.isArray(metadataIds) ? metadataIds : null;
  if (snapshot === null) return null;
  return [...new Set(snapshot.map(cleanText).filter(Boolean))].slice(0, 3);
};

export const getAutoPrintPointsForOrder = (order = {}, availablePoints = [], isSalon = false) => {
  const points = Array.isArray(availablePoints) ? availablePoints : [];
  if (!isSalon) return points;
  const snapshotIds = getTablePrintPointIds(order);
  // Old salon orders did not carry a snapshot. Keep their established behavior
  // while new orders (including an explicit empty snapshot) use the table rule.
  if (snapshotIds === null) return points;
  const allowedIds = new Set(snapshotIds);
  return points.filter((point) => allowedIds.has(cleanText(point?.id)));
};
